import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import EmptyState from '../../../components/pages/markets/component/empty-state';
import { useQuery, QueryClient, QueryClientProvider } from 'react-query';
import RenderCards from './render-cards';
import { formatDate, makeApiCall, isWithinTradingHours } from "../../../helpers/utils";
import { DATE_FORMATS } from "../../../constants";
import FilterControls from './filter-controls';
import get from "lodash/get";
import './budget-impacts-page.m.css';

export const getFromDate = (value = '') => {
  const now = new Date();
  if (value === '1d') {
    return new Date('2026-02-27');
  } else if (value === '1w') {
    return new Date('2026-02-21');
  } else if (value === '1m') {
    return new Date('2026-01-27');
  }
  return now;
};

const HeatmapSectorsPage = ({ features }) => {
  const queryClient = new QueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      <HeatmapSectorsPageContainer features={features} />
    </QueryClientProvider>
  );
};

const HeatmapSectorsPageContainer = ({ features }) => {
  const [cardsData, setCardsData] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState('low-to-high');
  const [compareRange, setCompareRange] = useState('1d');
  const [showFilters, setShowFilters] = useState(false);
  const tradingHolidays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'trading_holidays'], []),
  );
  const specialTradingDays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'special_trading_days'], {}),
  );

  const { data, isSuccess, refetch } = useQuery(
    ['getWarHeatmapSectorsData', compareRange],
    () => getSectorData(),
    {
      refetchInterval: 60000,
      refetchIntervalInBackground: true,
      enabled: initialLoading || isWithinTradingHours(tradingHolidays, specialTradingDays)
    }
  );

  useEffect(() => {
    if (isSuccess) {
      setCardsData(data);
      if (initialLoading) {
        setInitialLoading(false);
      }
    }
  }, [isSuccess, data]);

  useEffect(() => {
    if (!initialLoading) {
      refetch();
    }
  }, [compareRange]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const state = urlParams.get('state');

    if (state === 'war') {
      setShowFilters(true);
      getSectorData(true).then((res) => {
        setCardsData(res);
      });
    }
  }, []);

  const getSectorData = (refetch = false) => {
    let url;
    if (refetch || showFilters) {
      url = `${features?.heatmap?.sector_data_url}?fromDate=${formatDate(getFromDate(compareRange), DATE_FORMATS.DATE_TIME_REVERSE)}&toDate=${formatDate(getFromDate(), DATE_FORMATS.DATE_TIME_REVERSE)}`;
    } else {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      url = `${features?.heatmap?.sector_data_url}?fromDate=${formatDate(yesterday, DATE_FORMATS.DATE_TIME_REVERSE)}&toDate=${formatDate(new Date(), DATE_FORMATS.DATE_TIME_REVERSE)}`;
    }
    return makeApiCall(url)
      .then((res) => {
        if (!!res && res?.length > 0) {
          return res;
        } else {
          return [];
        }
      })
      .catch(() => {
        return [];
      });
  };

  const takeToSectorPage = (card) => {
    try {
      let url = `/markets/sectoral-heatmap/stocks?sector=${card?.sectorName?.replace(/\s/g, '_')?.replace(/&/g, '_')?.toLowerCase()}`;
      if (showFilters) {
        url += '&state=war';
      }
      window.open(url);
    } catch (e) {
      console.error('Error in redirection', e);
    }
  };

  return (
    <>
      <div styleName="budget-impacts-ctx" className="section-fluid">
        <div styleName="budget-impacts-inner-ctx" className="container">
          {/* Heading Section */}
          <div styleName="heatmap-headline-ctx">
            {initialLoading ? (
              <div styleName="title-subtitle">
                <div styleName="title-loading"></div>
                <div styleName="subtitle-loading"></div>
              </div>
            ) : (
              <React.Fragment>
                {showFilters ? (
                  <div styleName="title-subtitle">
                    <div styleName="title">{features?.heatmap?.texts?.custom?.title}</div>
                    <div styleName="subtitle" dangerouslySetInnerHTML={{__html: features?.heatmap?.texts?.custom?.subtitle.replace('#page', features?.heatmap?.texts?.sectors.toLowerCase())}}></div>
                  </div>
                ) : (
                  <div styleName="original-title-ctx mb-15">
                    <div styleName="title">{features?.heatmap?.texts?.default?.title}</div>
                    <div styleName="drop-list">{features?.heatmap?.texts?.sectors}</div>
                  </div>
                )}
              </React.Fragment>
            )}
            {showFilters && (
              <FilterControls
                sortOrder={sortOrder}
                setSortOrder={setSortOrder}
                compareRange={compareRange}
                setCompareRange={setCompareRange}
                getFromDate={getFromDate}
              />
            )}
          </div>

          {/* Show shimmer only on initial load */}
          {initialLoading ? (
            <>
              {/* Card Section Shimmer */}
              <div styleName="story-heatmap-ctx">
                <div styleName="cards-ctx">
                  <div styleName="cards">
                    {Array.apply(null, Array(16)).map((_, idx) => (
                      <div key={idx} styleName="card card-loading">
                        <div styleName="first-row"></div>
                        <div styleName="second-row"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Card Section */}
              <div styleName="story-heatmap-ctx">
                <div styleName="cards-ctx">
                  {cardsData.length === 0 ? (
                    <EmptyState width="280px" height="280px" />
                  ) : (
                    <div styleName="cards">{RenderCards(cardsData, takeToSectorPage, 'heatmap-sector-page', sortOrder )}</div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  features: state.qt.config.features,
});

export default connect(mapStateToProps)(HeatmapSectorsPage);
